import { configureStore } from '@reduxjs/toolkit'
import agenticHubReducer from '../features/agentic-hub/store/agenticHubSlice'
import dispatchHelpReducer from '../features/dispatch-help/store/dispatchHelpSlice'
import { loadTagsReducer } from '../features/load-tags/store/loadTagsSlice'
import { boxTruckLoadsReducer } from '../features/box-truck-loads/store/boxTruckLoadsSlice'
import accountingReducer from './accountingSlice'
import agentActivitiesReducer from './agentActivitiesSlice'
import authReducer from './authSlice'
import callCenterReducer from './callCenterSlice'
import contractorReducer from './contractorSlice'
import dashboardReducer from './dashboardSlice'
import driverReducer from './driverSlice'
import fleetReducer from './fleetSlice'
import loadAssistantReducer from './loadAssistantSlice'
import loadsReducer, { setSort } from './loadsSlice'
import { extensionSyncMiddleware } from './middleware/extensionSyncMiddleware'
import { attachBoardFilterPersistence } from './boardFilterSync'
import { persistOngoingLoadsSort, readPersistedOngoingLoadsSort } from './ongoingLoadsSortPersistence'
import { webSocketMiddleware } from './middleware/webSocketMiddleware'
import notificationReducer from './notificationSlice'
import ongoingLoadsReducer from './ongoingLoadsSlice'
import rtsJobReducer from './rtsJobSlice'
import safetyReducer from './safetySlice'
import schedulerReducer from './schedulerSlice'
import loadSearchReducer from './loadSearchSlice'
import tourReducer from './tourSlice'
import truckReducer from './truckSlice'
import trucksReducer from './trucksSlice'
import webSocketReducer from './webSocketSlice'

const rootReducer = {
    accounting: accountingReducer,
    loadSearch: loadSearchReducer,
    callCenter: callCenterReducer,
    notification: notificationReducer,
    webSocket: webSocketReducer,
    auth: authReducer,
    loadAssistant: loadAssistantReducer,
    scheduler: schedulerReducer,
    tour: tourReducer,
    ongoingLoads: ongoingLoadsReducer,
    loads: loadsReducer,
    driver: driverReducer,
    dashboard: dashboardReducer,
    fleet: fleetReducer,
    safety: safetyReducer,
    truck: truckReducer,
    trucks: trucksReducer,
    agentActivities: agentActivitiesReducer,
    contractor: contractorReducer,
    agenticHub: agenticHubReducer,
    dispatchHelp: dispatchHelpReducer,
    boxTruckLoads: boxTruckLoadsReducer,
    rtsJob: rtsJobReducer,
    loadTags: loadTagsReducer,
}

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredPaths: [
                    'callCenter.activeRingCentralCall.session',
                    'notification.notifications',
                    'loadData.loadData',
                    'updater.ongoingLoads',
                    'scheduler.drivers.data',
                    'scheduler.loads.data',
                    'scheduler.assignments.data',
                    'truck.trucks',
                ],

                // Ignore non-serializable values in WebSocket actions
                ignoredActions: [
                    'webSocket/connecting',
                    'webSocket/connected',
                    'webSocket/disconnected',
                    'callCenter/upsertRingCentralCallSession',
                    'notification/addNotification',
                    'notification/upsertNotification',
                ],
            },
        }).concat(webSocketMiddleware, extensionSyncMiddleware),
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// Restore the persisted Ongoing Loads sort, then keep localStorage in sync with it.
const persistedSort = readPersistedOngoingLoadsSort()
if (persistedSort) {
    store.dispatch(setSort(persistedSort))
}

let previousSort = store.getState().loads.sort
store.subscribe(() => {
    const currentSort = store.getState().loads.sort
    if (currentSort !== previousSort) {
        previousSort = currentSort
        persistOngoingLoadsSort(currentSort)
    }
})

// Restore the Ongoing Loads / Dispatch Board filters for the signed-in project, then keep
// localStorage in sync with them.
attachBoardFilterPersistence(store)

export default store
